import React, { useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  Filler
} from 'chart.js';
import { BarChart, LineChart, MixedChart } from '../components/charts';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import LicenseGate from '../components/ui/LicenseGate';
import KPICard from '../components/ui/KPICard';
import apiClient from '../utils/apiClient';
import { formatNumber, formatPercentage } from '../utils/formatters';
import { getChartColors } from '../utils/themeHelpers';
import { useTheme } from '../contexts/ThemeContext';
import { logger } from '../utils/logger';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  ChartLegend,
  Filler
);

interface BenchmarkRow {
  yearMonth: string | number;
  branchcount: number;
  yearBranchCount: number;
  ordercount: number;
  lagordercount: number;
  currentMonthForYearOrderCount: number;
  lastYearOrdercount: number;
  OrderCountGrowth: number;
  YearOrderCountGrowth: number;
  itemcount: number;
  lagitemcount: number;
  currentMonthForYearitemCount: number;
  lastYearitemcount: number;
  ItemCountGrowth: number;
  yearItemCountGrowth: number;
  itemPrice: number;
  lagitemprice: number;
  currentMonthForYearitemprice: number;
  lastYearitemprice: number;
  revenueGrowth: number;
  yearRevenueGrowth: number;
  orderAVG: number;
  lagorderAVG: number;
  yearOrderAvg: number;
  lastYearOrderAvg: number;
  orderAvgGrowth: number;
  yearOrderAvgGrowth: number;
  ItemAvg: number;
  LagItemAvg: number;
  CurrentYearItemAvg: number;
  lastYearItemAVG: number;
  AvgItemGrowth: number;
  YearItemAvgGrowth: number;
  ItemPriceAVG: number;
  lagItemPriceAVG: number;
  currentyearItemPriceAvg: number;
  LastYearItemPriceAvg: number;
  ItemPriceAvgGrowth: number;
  yearItemPriceAvgGrowth: number;
  inflation: number;
  foodInflation: number;
}

interface BenchmarkResponse {
  success: boolean;
  data: BenchmarkRow[];
  inflationRate?: number;
}

interface AnnualComparison {
  rows: BenchmarkRow[];
  orderGrowth: number;
  revenueGrowth: number;
  aovGrowth: number;
  unitPriceGrowth: number;
  itemsPerOrderGrowth: number;
  realRevenueGrowth: number;
  orders1404: number;
  orders1403: number;
  revenue1404: number;
  revenue1403: number;
  aov1404: number;
  aov1403: number;
  unitPrice1404: number;
  unitPrice1403: number;
  itemsPerOrder1404: number;
  itemsPerOrder1403: number;
  realRevenue1404: number;
}

type BenchmarkDashboardView = 'annual' | 'indicators' | 'monthly' | 'all';

interface BenchmarkDashboardProps {
  view?: BenchmarkDashboardView;
}

const DEFAULT_INFLATION_RATE = 0.48;
const CURRENCY = 'تومان';

const toNumber = (value: unknown): number => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const normalizeRate = (value: unknown): number => {
  const numericValue = toNumber(value);
  return Math.abs(numericValue) > 1 ? numericValue / 100 : numericValue;
};

const PERSIAN_MONTH_LABELS: Record<string, string> = {
  '01': 'فروردین',
  '02': 'اردیبهشت',
  '03': 'خرداد',
  '04': 'تیر',
  '05': 'مرداد',
  '06': 'شهریور',
  '07': 'مهر',
  '08': 'آبان',
  '09': 'آذر',
  '10': 'دی',
  '11': 'بهمن',
  '12': 'اسفند',
};

const formatMonth = (yearMonth: string | number): string => {
  const value = String(yearMonth);
  if (value.length !== 6) return value;
  const year = value.slice(2, 4);
  const month = value.slice(4, 6);
  return `${PERSIAN_MONTH_LABELS[month] || month} ${year}`;
};

const formatCurrencyCompact = (value: number): string => {
  const absValue = Math.abs(value);
  if (absValue >= 1_000_000_000_000) return `${(value / 1_000_000_000_000).toFixed(1)} همت`;
  if (absValue >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)} میلیارد`;
  if (absValue >= 1_000_000) return `${(value / 1_000_000).toFixed(1)} میلیون`;
  return formatNumber(Math.round(value));
};

const formatCurrency = (value: number): string => `${formatNumber(Math.round(value))} ${CURRENCY}`;

const formatMetricValue = (value: number, type: 'count' | 'money' | 'decimal' = 'count'): string => {
  if (type === 'money') return formatCurrency(value);
  if (type === 'decimal') return value.toFixed(2);
  return formatNumber(Math.round(value));
};

const getYear = (yearMonth: string | number): string => String(yearMonth).slice(0, 4);

const safeDivide = (numerator: number, denominator: number): number => (
  denominator === 0 ? 0 : numerator / denominator
);

const calculateGrowth = (current: number, previous: number): number => (
  previous === 0 ? 0 : (current / previous) - 1
);

const weightedAverage = (rows: BenchmarkRow[], valueSelector: (row: BenchmarkRow) => number, weightSelector: (row: BenchmarkRow) => number): number => {
  const totalWeight = rows.reduce((sum, row) => sum + weightSelector(row), 0);
  if (totalWeight === 0) return 0;
  return rows.reduce((sum, row) => sum + valueSelector(row) * weightSelector(row), 0) / totalWeight;
};

const withoutGridLines = (options: any = {}) => ({
  ...options,
  scales: {
    ...options.scales,
    x: {
      ...options.scales?.x,
      grid: {
        ...options.scales?.x?.grid,
        display: false,
      },
    },
    y: {
      ...options.scales?.y,
      grid: {
        ...options.scales?.y?.grid,
        display: false,
      },
    },
  },
});

const getMonthlyInflationRate = (row: BenchmarkRow): number => (
  row.foodInflation || row.inflation || 0
);

const getRealRevenue = (row: BenchmarkRow, revenueField: 'itemPrice' | 'currentMonthForYearitemprice' = 'currentMonthForYearitemprice'): number => (
  safeDivide(row[revenueField], 1 + getMonthlyInflationRate(row))
);

const normalizeRow = (row: BenchmarkRow): BenchmarkRow => ({
  yearMonth: row.yearMonth,
  branchcount: toNumber(row.branchcount),
  yearBranchCount: toNumber(row.yearBranchCount),
  ordercount: toNumber(row.ordercount),
  lagordercount: toNumber(row.lagordercount),
  currentMonthForYearOrderCount: toNumber(row.currentMonthForYearOrderCount),
  lastYearOrdercount: toNumber(row.lastYearOrdercount),
  OrderCountGrowth: toNumber(row.OrderCountGrowth),
  YearOrderCountGrowth: toNumber(row.YearOrderCountGrowth),
  itemcount: toNumber(row.itemcount),
  lagitemcount: toNumber(row.lagitemcount),
  currentMonthForYearitemCount: toNumber(row.currentMonthForYearitemCount),
  lastYearitemcount: toNumber(row.lastYearitemcount),
  ItemCountGrowth: toNumber(row.ItemCountGrowth),
  yearItemCountGrowth: toNumber(row.yearItemCountGrowth),
  itemPrice: toNumber(row.itemPrice),
  lagitemprice: toNumber(row.lagitemprice),
  currentMonthForYearitemprice: toNumber(row.currentMonthForYearitemprice),
  lastYearitemprice: toNumber(row.lastYearitemprice),
  revenueGrowth: toNumber(row.revenueGrowth),
  yearRevenueGrowth: toNumber(row.yearRevenueGrowth),
  orderAVG: toNumber(row.orderAVG),
  lagorderAVG: toNumber(row.lagorderAVG),
  yearOrderAvg: toNumber(row.yearOrderAvg),
  lastYearOrderAvg: toNumber(row.lastYearOrderAvg),
  orderAvgGrowth: toNumber(row.orderAvgGrowth),
  yearOrderAvgGrowth: toNumber(row.yearOrderAvgGrowth),
  ItemAvg: toNumber(row.ItemAvg),
  LagItemAvg: toNumber(row.LagItemAvg),
  CurrentYearItemAvg: toNumber(row.CurrentYearItemAvg),
  lastYearItemAVG: toNumber(row.lastYearItemAVG),
  AvgItemGrowth: toNumber(row.AvgItemGrowth),
  YearItemAvgGrowth: toNumber(row.YearItemAvgGrowth),
  ItemPriceAVG: toNumber(row.ItemPriceAVG),
  lagItemPriceAVG: toNumber(row.lagItemPriceAVG),
  currentyearItemPriceAvg: toNumber(row.currentyearItemPriceAvg),
  LastYearItemPriceAvg: toNumber(row.LastYearItemPriceAvg),
  ItemPriceAvgGrowth: toNumber(row.ItemPriceAvgGrowth),
  yearItemPriceAvgGrowth: toNumber(row.yearItemPriceAvgGrowth),
  inflation: normalizeRate(row.inflation),
  foodInflation: normalizeRate(row.foodInflation),
});

const BenchmarkDashboard: React.FC<BenchmarkDashboardProps> = ({ view = 'all' }) => {
  const { currentTheme } = useTheme();
  const colors = getChartColors(currentTheme);
  const [rows, setRows] = useState<BenchmarkRow[]>([]);
  const [inflationRate, setInflationRate] = useState<number>(DEFAULT_INFLATION_RATE);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBenchmarkData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiClient.get<BenchmarkResponse>('/benchmark/market');
        const benchmarkRows = (response.data.data || [])
          .map(normalizeRow)
          .sort((first, second) => String(first.yearMonth).localeCompare(String(second.yearMonth)));

        setRows(benchmarkRows);
        setInflationRate(response.data.inflationRate ?? DEFAULT_INFLATION_RATE);
      } catch (err) {
        logger.error('Error fetching benchmark dashboard data', 'BenchmarkDashboard', err);
        setError('خطا در بارگذاری داده‌های تحلیل نبض بازار');
      } finally {
        setLoading(false);
      }
    };

    fetchBenchmarkData();
  }, []);

  const latest = rows[rows.length - 1];
  const labels = useMemo(() => rows.map(row => formatMonth(row.yearMonth)), [rows]);
  const rows1404 = useMemo(() => rows.filter(row => getYear(row.yearMonth) === '1404'), [rows]);
  const annualRows = rows1404.length > 0 ? rows1404 : rows;
  const effectiveAnnualInflationRate = useMemo(() => {
    if (annualRows.length === 0) return inflationRate;
    // Calculate compound inflation: (1+r1)(1+r2)...(1+rn) - 1
    const compoundRate = annualRows.reduce((product, row) => {
      return product * (1 + getMonthlyInflationRate(row));
    }, 1);
    return compoundRate - 1;
  }, [annualRows, inflationRate]);

  const annualComparison = useMemo<AnnualComparison>(() => {
    const orders1404 = annualRows.reduce((sum, row) => sum + row.currentMonthForYearOrderCount, 0);
    const orders1403 = annualRows.reduce((sum, row) => sum + row.lastYearOrdercount, 0);
    const revenue1404 = annualRows.reduce((sum, row) => sum + row.currentMonthForYearitemprice, 0);
    const revenue1403 = annualRows.reduce((sum, row) => sum + row.lastYearitemprice, 0);
    const aov1404 = weightedAverage(annualRows, row => row.yearOrderAvg, row => row.currentMonthForYearOrderCount);
    const aov1403 = weightedAverage(annualRows, row => row.lastYearOrderAvg, row => row.lastYearOrdercount);
    const unitPrice1404 = weightedAverage(annualRows, row => row.currentyearItemPriceAvg, row => row.currentMonthForYearitemCount);
    const unitPrice1403 = weightedAverage(annualRows, row => row.LastYearItemPriceAvg, row => row.lastYearitemcount);
    const itemsPerOrder1404 = weightedAverage(annualRows, row => row.CurrentYearItemAvg, row => row.currentMonthForYearOrderCount);
    const itemsPerOrder1403 = weightedAverage(annualRows, row => row.lastYearItemAVG, row => row.lastYearOrdercount);
    const realRevenue1404 = annualRows.reduce((sum, row) => sum + getRealRevenue(row, 'currentMonthForYearitemprice'), 0);

    return {
      rows: annualRows,
      orderGrowth: calculateGrowth(orders1404, orders1403),
      revenueGrowth: calculateGrowth(revenue1404, revenue1403),
      aovGrowth: calculateGrowth(aov1404, aov1403),
      unitPriceGrowth: calculateGrowth(unitPrice1404, unitPrice1403),
      itemsPerOrderGrowth: calculateGrowth(itemsPerOrder1404, itemsPerOrder1403),
      realRevenueGrowth: calculateGrowth(realRevenue1404, revenue1403),
      orders1404,
      orders1403,
      revenue1404,
      revenue1403,
      aov1404,
      aov1403,
      unitPrice1404,
      unitPrice1403,
      itemsPerOrder1404,
      itemsPerOrder1403,
      realRevenue1404,
    };
  }, [annualRows]);

  const chartNumberOptions = useMemo(() => ({
    plugins: {
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${formatMetricValue(context.raw, context.dataset.metricType || 'count')}`
        }
      }
    },
    scales: {
      y: {
        ticks: {
          callback: (value: string | number) => formatCurrencyCompact(Number(value))
        }
      }
    }
  }), []);

  const percentChartOptions = useMemo(() => ({
    plugins: {
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${formatPercentage(Number(context.raw) * 100)}`
        }
      }
    },
    scales: {
      y: {
        ticks: {
          callback: (value: string | number) => `${Number(value) * 100}%`
        }
      }
    }
  }), []);

  const barChartNumberOptions = useMemo(() => withoutGridLines(chartNumberOptions), [chartNumberOptions]);
  const barChartPercentOptions = useMemo(() => withoutGridLines(percentChartOptions), [percentChartOptions]);
  const noGridNumberOptions = barChartNumberOptions;
  const noGridPercentOptions = barChartPercentOptions;

  const revenueData = useMemo(() => ({
    labels,
    datasets: [
      {
        type: 'bar' as const,
        label: 'فروش کل',
        data: rows.map(row => row.itemPrice),
        backgroundColor: `${colors[2]}99`,
        borderColor: colors[2],
        borderWidth: 1,
        metricType: 'money'
      },
      {
        type: 'line' as const,
        label: 'درآمد بدون افزایش قیمت‌ها',
        data: rows.map(row => getRealRevenue(row)),
        borderColor: colors[4],
        backgroundColor: `${colors[4]}33`,
        borderWidth: 3,
        tension: 0.35,
        fill: false,
        metricType: 'money'
      }
    ]
  }), [colors, labels, rows]);

  const growthData = useMemo(() => ({
    labels,
    datasets: [
      {
        label: 'رشد سفارش',
        data: rows.map(row => row.OrderCountGrowth),
        backgroundColor: `${colors[0]}99`,
        borderColor: colors[0],
        borderWidth: 1
      },
      {
        label: 'رشد فروش',
        data: rows.map(row => row.revenueGrowth),
        backgroundColor: `${colors[3]}99`,
        borderColor: colors[3],
        borderWidth: 1
      }
    ]
  }), [colors, labels, rows]);

  const annualGrowthData = useMemo(() => ({
    labels: [
      'تعداد سفارشات',
      'فروش کل',
      'مبلغ هر سفارش',
      'قیمت هر واحد کالا',
      'اقلام هر سفارش',
      'تورم سالانه',
      'درآمد بدون افزایش قیمت‌ها'
    ],
    datasets: [
      {
        label: 'نرخ تغییر ۱۴۰۴ نسبت به ۱۴۰۳',
        data: [
          annualComparison.orderGrowth,
          annualComparison.revenueGrowth,
          annualComparison.aovGrowth,
          annualComparison.unitPriceGrowth,
          annualComparison.itemsPerOrderGrowth,
          effectiveAnnualInflationRate,
          annualComparison.realRevenueGrowth,
        ],
        backgroundColor: [
          `${colors[0]}99`,
          `${colors[2]}99`,
          `${colors[5]}99`,
          `${colors[6]}99`,
          `${colors[7]}99`,
          `${colors[3]}99`,
          `${colors[4]}99`,
        ],
        borderColor: [colors[0], colors[2], colors[5], colors[6], colors[7], colors[3], colors[4]],
        borderWidth: 1,
      }
    ]
  }), [annualComparison, colors, effectiveAnnualInflationRate]);
  const paradoxChartData = useMemo(() => ({
    labels: ['تغییرات شاخص‌ها'],
    datasets: [
      {
        type: 'bar' as const,
        label: 'تغییرات فروش',
        data: [annualComparison.revenueGrowth],
        backgroundColor: `${colors[5]}99`,
        borderColor: colors[5],
        borderWidth: 2,
      },
      {
        type: 'bar' as const,
        label: 'تغییرات تعداد سفارشات',
        data: [annualComparison.orderGrowth],
        backgroundColor: `${colors[2]}99`,
        borderColor: colors[2],
        borderWidth: 2,
      },
      {
        type: 'bar' as const,
        label: 'تغییرات فروش بدون افزایش قیمت‌ها',
        data: [annualComparison.realRevenueGrowth],
        backgroundColor: `${colors[1]}99`,
        borderColor: colors[1],
        borderWidth: 2,
      },
    ],
  }), [annualComparison, colors]);


  const independentRows = rows1404.length > 0 ? rows1404 : rows;
  const independentLabels = useMemo(() => independentRows.map(row => formatMonth(row.yearMonth)), [independentRows]);

  const independentVolumeData = useMemo(() => ({
    labels: independentLabels,
    datasets: [
      {
        label: 'تعداد سفارشات',
        data: independentRows.map(row => row.ordercount),
        borderColor: colors[0],
        backgroundColor: `${colors[0]}44`,
        tension: 0.35,
        fill: false,
        metricType: 'count'
      },
      {
        label: 'تعداد اقلام',
        data: independentRows.map(row => row.itemcount),
        borderColor: colors[1],
        backgroundColor: `${colors[1]}44`,
        tension: 0.35,
        fill: false,
        metricType: 'count'
      }
    ]
  }), [colors, independentLabels, independentRows]);

  const independentValueData = useMemo(() => ({
    labels: independentLabels,
    datasets: [
      {
        label: 'مبلغ هر سفارش',
        data: independentRows.map(row => row.orderAVG),
        borderColor: colors[5],
        backgroundColor: `${colors[5]}44`,
        tension: 0.35,
        fill: false,
        metricType: 'money'
      },
      {
        label: 'قیمت هر واحد کالا',
        data: independentRows.map(row => row.ItemPriceAVG),
        borderColor: colors[6],
        backgroundColor: `${colors[6]}44`,
        tension: 0.35,
        fill: false,
        metricType: 'money'
      }
    ]
  }), [colors, independentLabels, independentRows]);

  const monthlyMarketData = useMemo(() => ({
    labels,
    datasets: [
      {
        type: 'bar' as const,
        label: 'فروش کل',
        data: rows.map(row => row.itemPrice),
        backgroundColor: `${colors[2]}88`,
        borderColor: colors[2],
        borderWidth: 1,
        metricType: 'money'
      },
      {
        type: 'line' as const,
        label: 'تعداد سفارشات',
        data: rows.map(row => row.ordercount),
        borderColor: colors[0],
        backgroundColor: `${colors[0]}33`,
        borderWidth: 3,
        tension: 0.35,
        fill: false,
        metricType: 'count'
      },
      {
        type: 'line' as const,
        label: 'درآمد بدون افزایش قیمت‌ها',
        data: rows.map(row => getRealRevenue(row)),
        borderColor: colors[4],
        backgroundColor: `${colors[4]}33`,
        borderWidth: 3,
        tension: 0.35,
        fill: false,
        metricType: 'money'
      }
    ]
  }), [colors, labels, rows]);

  const monthlyInflationData = useMemo(() => ({
    labels,
    datasets: [
      {
        label: 'تورم غذا',
        data: rows.map(row => row.foodInflation || 0),
        borderColor: colors[3],
        backgroundColor: `${colors[3]}44`,
        tension: 0.35,
        fill: false,
      },
      {
        label: 'تورم عمومی',
        data: rows.map(row => row.inflation || 0),
        borderColor: colors[6],
        backgroundColor: `${colors[6]}44`,
        tension: 0.35,
        fill: false,
      }
    ]
  }), [colors, labels, rows]);

  const monthlyGrowthReports = useMemo(() => rows.map((row, index) => {
    const previousRow = index > 0 ? rows[index - 1] : null;
    const realRevenueGrowth = previousRow
      ? calculateGrowth(getRealRevenue(row), getRealRevenue(previousRow))
      : 0;

    return {
      month: formatMonth(row.yearMonth),
      data: {
        labels: [
          'تعداد سفارشات',
          'فروش کل',
          'مبلغ هر سفارش',
          'قیمت واحد کالا',
          'اقلام هر سفارش',
          'درآمد بدون افزایش قیمت‌ها',
          'تورم'
        ],
        datasets: [{
          label: 'نرخ رشد / تورم',
          data: [
            row.OrderCountGrowth,
            row.revenueGrowth,
            row.orderAvgGrowth,
            row.ItemPriceAvgGrowth,
            row.AvgItemGrowth,
            realRevenueGrowth,
            getMonthlyInflationRate(row),
          ],
          backgroundColor: [
            `${colors[0]}99`,
            `${colors[2]}99`,
            `${colors[5]}99`,
            `${colors[6]}99`,
            `${colors[7]}99`,
            `${colors[4]}99`,
            `${colors[3]}99`,
          ],
          borderColor: [colors[0], colors[2], colors[5], colors[6], colors[7], colors[4], colors[3]],
          borderWidth: 1,
        }]
      }
    };
  }), [colors, rows]);

  const annualMetricRows = [
    {
      title: 'تعداد سفارشات',
      current: formatMetricValue(annualComparison.orders1404),
      previous: formatMetricValue(annualComparison.orders1403),
      growth: annualComparison.orderGrowth,
    },
    {
      title: 'فروش کل',
      current: formatCurrencyCompact(annualComparison.revenue1404),
      previous: formatCurrencyCompact(annualComparison.revenue1403),
      growth: annualComparison.revenueGrowth,
    },
    {
      title: 'مبلغ هر سفارش (AOV)',
      current: formatCurrency(annualComparison.aov1404),
      previous: formatCurrency(annualComparison.aov1403),
      growth: annualComparison.aovGrowth,
    },
    {
      title: 'قیمت هر واحد کالا',
      current: formatCurrency(annualComparison.unitPrice1404),
      previous: formatCurrency(annualComparison.unitPrice1403),
      growth: annualComparison.unitPriceGrowth,
    },
    {
      title: 'تعداد اقلام هر سفارش',
      current: formatMetricValue(annualComparison.itemsPerOrder1404, 'decimal'),
      previous: formatMetricValue(annualComparison.itemsPerOrder1403, 'decimal'),
      growth: annualComparison.itemsPerOrderGrowth,
    },
    {
      title: 'تورم سالانه',
      current: formatPercentage(effectiveAnnualInflationRate * 100),
      previous: '-',
      growth: effectiveAnnualInflationRate,
    },
    {
      title: 'درآمد بدون افزایش قیمت‌ها',
      current: formatCurrencyCompact(annualComparison.realRevenue1404),
      previous: formatCurrencyCompact(annualComparison.revenue1403),
      growth: annualComparison.realRevenueGrowth,
    },
  ];

  const paradoxGap = annualComparison.revenueGrowth - annualComparison.orderGrowth;
  const inflationGap = annualComparison.revenueGrowth - effectiveAnnualInflationRate;
  const pricingGap = annualComparison.unitPriceGrowth - effectiveAnnualInflationRate;

  const storyCards = [
    {
      title: 'پارادوکس رشد',
      value: formatPercentage(paradoxGap * 100),
      subtitle: `فروش ${formatPercentage(annualComparison.revenueGrowth * 100)} در برابر سفارش ${formatPercentage(annualComparison.orderGrowth * 100)}`,
      description: 'اگر فروش سریع‌تر از سفارش رشد کرده باشد، بازار از رشد قیمتی تغذیه شده؛ اگر عقب‌تر باشد، حجم سفارش به درآمد کافی تبدیل نشده است.',
      variant: 'primary' as const
    },
    {
      title: 'جاماندگی درآمد از تورم',
      value: formatPercentage(inflationGap * 100),
      subtitle: `رشد فروش ${formatPercentage(annualComparison.revenueGrowth * 100)} در برابر تورم ${formatPercentage(effectiveAnnualInflationRate * 100)}`,
      description: 'شکاف منفی یعنی درآمد اسمی بازار نتوانسته قدرت خرید سال قبل را حفظ کند.',
      variant: 'error' as const
    },
    {
      title: 'فدا کردن حاشیه سود برای حفظ مشتری',
      value: formatPercentage(pricingGap * 100),
      subtitle: `رشد قیمت واحد ${formatPercentage(annualComparison.unitPriceGrowth * 100)} در برابر تورم ${formatPercentage(effectiveAnnualInflationRate * 100)}`,
      description: 'وقتی قیمت واحد کمتر از تورم رشد می‌کند، بازار بخشی از فشار هزینه را برای حفظ مشتری جذب کرده است.',
      variant: 'warning' as const
    }
  ];

  const dashboardTitleMap: Record<BenchmarkDashboardView, string> = {
    annual: 'تحلیل سالانه نبض بازار',
    indicators: 'شاخص‌های مستقل نبض بازار',
    monthly: 'روند ماهانه نبض بازار',
    all: 'تحلیل نبض بازار',
  };

  const dashboardSubtitleMap: Record<BenchmarkDashboardView, string> = {
    annual: 'مقایسه وضعیت کلی سال ۱۴۰۴ با سال ۱۴۰۳ و بررسی پارادوکس رشد، تورم و حاشیه سود.',
    indicators: 'تمرکز روی رفتار مستقل سفارش، فروش، AOV، قیمت واحد کالا و اقلام هر سفارش در طول سال.',
    monthly: 'روند و رشد ماه‌به‌ماه شاخص‌های اصلی بازار کافه و رستوران.',
    all: 'روایت سه‌فصلی از بازار کافه و رستوران، مستقل از برند و شعبه.',
  };

  const showAnnual = view === 'all' || view === 'annual';
  const showIndicators = view === 'all' || view === 'indicators';
  const showMonthly = view === 'all' || view === 'monthly';

  return (
    <LicenseGate licenseType="evaluation" dashboardName="تحلیل نبض بازار">
      <div className="container-dashboard" dir="rtl">
        <div className="space-y-8 md:space-y-10 animate-enter-up">
          {loading ? (
            <div className="flex flex-col items-center justify-center min-h-[300px]" role="status" aria-live="polite">
              <LoadingSpinner
                variant="minimal"
                size="lg"
                message="در حال بارگذاری داده‌های تحلیل بازار…"
              />
              <p className="mt-4 text-sm content-secondary">لطفاً چند لحظه صبر کنید…</p>
            </div>
          ) : error ? (
            <div className="p-4 m-6 bg-status-error text-text-inverse rounded-lg shadow-md animate-enter" role="alert" aria-live="assertive">
              <div className="flex items-center">
                <span className="font-medium">{error}</span>
              </div>
            </div>
          ) : rows.length === 0 ? (
            <div className="card-base card-padding text-center">
              <h1 className="title-dashboard">تحلیل نبض بازار</h1>
              <p className="content-secondary">داده‌ای برای نمایش وجود ندارد.</p>
            </div>
          ) : (
            <>
              <div>
                <h1 className="title-dashboard">{dashboardTitleMap[view]}</h1>
                <p className="content-secondary mt-2">
                  {dashboardSubtitleMap[view]} آخرین ماه داده: {formatMonth(latest.yearMonth)}
                </p>
              </div>

              {showAnnual && <section className="space-y-6">
                <div className="card-base card-padding">
                  <div className="mb-5">
                    <p className="text-sm text-text-tertiary mb-2">فصل اول</p>
                    <h2 className="title-section">وضعیت کلی سال ۱۴۰۴ در مقایسه با سال ۱۴۰۳</h2>
                    <p className="content-secondary mt-2">
                      این فصل عملکرد بازار را با ۷ شاخص کلیدی مقایسه می‌کند و نشان می‌دهد رشد اسمی تا چه حد از رشد واقعی و تورم فاصله دارد.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                    {annualMetricRows.map(metric => (
                      <KPICard
                        key={metric.title}
                        title={metric.title}
                        value={metric.current}
                        subtitle={`۱۴۰۳: ${metric.previous} | تغییر: ${formatPercentage(metric.growth * 100)}`}
                        variant={metric.growth >= 0 ? 'success' : 'error'}
                        accentStyle="gradient"
                      />
                    ))}
                  </div>
                  <div className="mt-6 p-5 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-sm text-blue-800 dark:text-blue-200 leading-7">
                      <span className="font-semibold">تحلیل وضعیت:</span> {
                        annualComparison.realRevenueGrowth < 0 
                          ? `درآمد واقعی بازار ${formatPercentage(annualComparison.realRevenueGrowth * 100)} کاهش یافته که نشان‌دهنده کوچک‌تر شدن بازار است.`
                          : `درآمد واقعی بازار ${formatPercentage(annualComparison.realRevenueGrowth * 100)} رشد داشته است.`
                      } تورم سالانه {formatPercentage(effectiveAnnualInflationRate * 100)} است.
                    </p>
                  </div>
                </div>

              <div className="card-base card-padding">
                <h3 className="title-card mb-4">پارادوکس رشد: تحلیل تغییرات کلیدی</h3>
                <BarChart
                  title=""
                  data={paradoxChartData}
                  options={barChartPercentOptions}
                  height={300}
                  className=""
                />
                <div className="mt-6 p-5 bg-bg-secondary/50 rounded-lg border-border-secondary">
                  <p className="content-secondary leading-8 text-justify">
                    <span className="font-semibold text-text-primary">نتیجه‌گیری:</span> افزایش فروش (نمودار سبز) لزوماً خبر خوبی نیست. وقتی اثر افزایش قیمت منو را از آن حذف کنیم، فروش واقعی (نمودار نارنجی) منفی می‌شود. یعنی قدرت خرید واقعی (نمودار سرمه‌ای) کاهش یافته.
                  </p>
                  <p className="content-secondary leading-8 text-justify mt-4">
                    به تعبیر دیگر می‌توان گفت در کل سال بازار {formatPercentage(annualComparison.realRevenueGrowth * 100)} کوچک‌تر شده است یا به تعبیر دیگر پول کمتری در بازار چرخیده است.
                  </p>
                  <div className="mt-5 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <p className="text-sm text-amber-800 dark:text-amber-200 leading-7">
                      <span className="font-semibold">نکته تحلیلی:</span> وقتی رشد فروش اسمی (سبز) بالاتر از رشد تعداد سفارشات (سرمه‌ای) است، نشان‌دهنده تورم قیمت منو است. اما وقتی درآمد واقعی (نارنجی) منفی باشد، یعنی بازار در واقع کوچک شده و قدرت خرید مصرف‌کننده کاهش یافته است. این پارادوکس نشان‌دهنده فشار تورمی بر بازار است.
                    </p>
                  </div>
                  <div className="mt-5 flex items-center gap-6 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="w-4 h-4 rounded" style={{ backgroundColor: colors[5] }}></span>
                      <span className="text-sm text-text-secondary">تغییرات فروش (سبز)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-4 h-4 rounded" style={{ backgroundColor: colors[2] }}></span>
                      <span className="text-sm text-text-secondary">تغییرات سفارشات (سرمه‌ای)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-4 h-4 rounded" style={{ backgroundColor: colors[1] }}></span>
                      <span className="text-sm text-text-secondary">فروش بدون افزایش قیمت‌ها (نارنجی)</span>
                    </div>
                  </div>
                </div>
              </div>

                <div className="grid grid-cols-1 gap-6">
                  <BarChart
                    title="نرخ تغییر شاخص‌های کلیدی ۱۴۰۴ نسبت به ۱۴۰۳"
                    data={annualGrowthData}
                    options={barChartPercentOptions}
                    height={360}
                    className="card-base card-padding"
                  />
                </div>
              </section>}
              
              {/* <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                  {storyCards.map(card => (
                    <div key={card.title} className="card-base card-padding">
                      <KPICard
                        title={card.title}
                        value={card.value}
                        subtitle={card.subtitle}
                        variant={card.variant}
                        accentStyle="corner"
                      />
                      <p className="content-secondary mt-4 leading-7">{card.description}</p>
                    </div>
                  ))}
                </div> */}


              {showIndicators && <section className="space-y-6">
                <div className="card-base card-padding">
                  <p className="text-sm text-text-tertiary mb-2">فصل دوم</p>
                  <h2 className="title-section">تحلیل مستقل برخی شاخص‌ها در طول سال ۱۴۰۴</h2>
                  <p className="content-secondary mt-2">
                    در این فصل هر شاخص جداگانه دیده می‌شود تا رفتار مستقل سفارش، ارزش سبد خرید، قیمت واحد کالا و تعداد اقلام هر سفارش مشخص شود.
                  </p>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  <LineChart
                    title="شاخص‌های حجمی: سفارش و اقلام"
                    data={independentVolumeData}
                    options={noGridNumberOptions}
                    height={360}
                    className="card-base card-padding"
                  />
                  <LineChart
                    title="شاخص‌های ارزشی: AOV و قیمت واحد کالا"
                    data={independentValueData}
                    options={noGridNumberOptions}
                    height={360}
                    className="card-base card-padding"
                  />
                  <BarChart
                    title="تعداد اقلام هر سفارش در سال ۱۴۰۴"
                    data={{
                      labels: independentLabels,
                      datasets: [{
                        label: 'میانگین اقلام هر سفارش',
                        data: independentRows.map(row => row.ItemAvg || row.CurrentYearItemAvg),
                        backgroundColor: `${colors[7]}99`,
                        borderColor: colors[7],
                        borderWidth: 1,
                        metricType: 'decimal'
                      }]
                    }}
                    options={withoutGridLines({
                      ...barChartNumberOptions,
                      scales: {
                        ...barChartNumberOptions.scales,
                        y: {
                          ...barChartNumberOptions.scales?.y,
                          ticks: {
                            callback: (value: string | number) => Number(value).toFixed(2)
                          }
                        }
                      }
                    })}
                    height={340}
                    className="card-base card-padding"
                  />
                  <MixedChart
                    title="فروش کل و درآمد تعدیل‌شده با تورم"
                    data={revenueData}
                    options={noGridNumberOptions}
                    height={340}
                    className="card-base card-padding"
                  />
                </div>
              </section>}

              {showMonthly && <section className="space-y-6">
                <div className="card-base card-padding">
                  <p className="text-sm text-text-tertiary mb-2">فصل سوم</p>
                  <h2 className="title-section">تحلیل روند ماهانه در بازار کافه و رستوران</h2>
                  <p className="content-secondary mt-2">
                    این فصل گزارش ماه‌به‌ماه شاخص‌های اصلی بازار را نشان می‌دهد و درآمد بدون افزایش قیمت‌ها را با تورم همان ماه محاسبه می‌کند.
                  </p>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  <MixedChart
                    title="روند ماهانه فروش، سفارش و درآمد بدون افزایش قیمت‌ها"
                    data={monthlyMarketData}
                    options={noGridNumberOptions}
                    height={380}
                    className="card-base card-padding"
                  />
                  <BarChart
                    title="رشد ماهانه سفارش و فروش"
                    data={growthData}
                    options={barChartPercentOptions}
                    height={380}
                    className="card-base card-padding"
                  />
                  <LineChart
                    title="تورم ماهانه: عمومی و غذا"
                    data={monthlyInflationData}
                    options={noGridPercentOptions}
                    height={320}
                    className="card-base card-padding xl:col-span-2"
                  />
                </div>

                <div className="card-base card-padding">
                  <h3 className="title-card mb-2">رشد ماه‌به‌ماه شاخص‌های بازار</h3>
                  <p className="content-secondary mb-6">
                    برای هر ماه، هر ستون نشان‌دهنده نرخ رشد همان شاخص نسبت به ماه قبل است؛ ستون تورم مقدار تورم همان ماه را نشان می‌دهد.
                  </p>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    {[...monthlyGrowthReports].reverse().map(report => (
                      <BarChart
                        key={report.month}
                        title={report.month}
                        data={report.data}
                        options={barChartPercentOptions}
                        height={300}
                        className="card-base card-padding"
                      />
                    ))}
                  </div>
                </div>
              </section>}
            </>
          )}
        </div>
      </div>
    </LicenseGate>
  );
};

export default BenchmarkDashboard;
